// Training library — what /training shows for each dashboard tile: the short
// in-app help topic (src/help/topics/), the tile's full section of the user
// manual (docs/manuals/), and its video slot (src/help/trainingClips.ts).
//
// The page renders the manual chapters themselves, never a copy, so a manual
// edit reaches the app on the next deploy with nothing to regenerate. A tile's
// section is the `## Label (`/route`)` heading that names its route — the rule
// scripts/check-manual-coverage.mjs already enforces, so every tile has one.
//
// Each chapter is a lazy raw chunk kept OUT of the service-worker precache
// (vite.config.ts globIgnores): 1.5 MB of prose would ride every tablet's
// install for a page most operators open now and then. The runtime
// CacheFirst rule beside it keeps a chapter offline once it has been opened.

import { MODULES, moduleKindOfCategory, type ModuleDef, type ModuleOwningKind } from '@/lib/modules'
import { loadHelpTopic, parseFrontmatter } from '@/lib/helpTopics'
import { AF_MARKER, fingerprint, headingSlug, parseMarker, rawSections } from '@/lib/afrikaansMarks.mjs'

/**
 * Tenant kinds whose dashboard carries the Training tile — the packhouse, farm
 * and exporter apps. PAYROLL is the packhouse app's payroll home: its tiles
 * (Labour, Teams, Users) are packhouse tiles with packhouse chapters.
 */
const TRAINING_TENANT_KINDS: readonly string[] = ['PACKHOUSE', 'PAYROLL', 'FARM', 'EXPORTER']

export function offersTraining(kind: string | null | undefined): boolean {
  return !!kind && TRAINING_TENANT_KINDS.includes(kind)
}

/** The /training/:guideId id of the Getting started chapter (not a tile). */
export const GETTING_STARTED_ID = 'getting-started'

/** A tile's help topic id — the D9 convention helpTopics.ts resolves by. */
export function trainingTopicId(tile: ModuleDef): string {
  return tile.helpTopicId ?? tile.key
}

// Chapter file-name prefix per tenant kind (docs/manuals/README.md's layout).
const CHAPTER_PREFIX: Partial<Record<ModuleOwningKind, string>> = {
  PACKHOUSE: 'PACKHOUSE-',
  FARM:      'FARM-',
  EXPORTER:  'EXPORTER-',
}
const GETTING_STARTED_FILE = 'GETTING-STARTED.md'

const chapterFiles = import.meta.glob(
  [
    '/docs/manuals/PACKHOUSE-*.md',
    '/docs/manuals/FARM-*.md',
    '/docs/manuals/EXPORTER-*.md',
    '/docs/manuals/GETTING-STARTED.md',
  ],
  { query: '?raw', import: 'default' },
) as Record<string, () => Promise<string>>

const fileOf = (p: string) => p.replace(/^.*\//, '')

// ---------------------------------------------------------------------------
// Pure markdown helpers (exported for the unit tests)
// ---------------------------------------------------------------------------

export interface MarkdownSection {
  heading: string
  body:    string
}

/**
 * Split markdown at its `## ` (level 2) or `### ` (level 3) headings. `lead`
 * is everything before the first one. A heading-looking line inside a code
 * fence is not a heading. A section's closing `---` rule is dropped — it
 * separates sections on GitHub and would rule off nothing here.
 */
export function splitSections(md: string, level: 2 | 3): { lead: string; sections: MarkdownSection[] } {
  const marker = level === 2 ? '## ' : '### '
  const lead: string[] = []
  const sections: { heading: string; lines: string[] }[] = []
  let inFence = false
  for (const line of md.split('\n')) {
    if (line.startsWith('```')) inFence = !inFence
    if (!inFence && line.startsWith(marker)) {
      sections.push({ heading: line.slice(marker.length).trim(), lines: [] })
    } else if (sections.length > 0) {
      sections[sections.length - 1].lines.push(line)
    } else {
      lead.push(line)
    }
  }
  const tidy = (lines: string[]) => lines.join('\n').replace(/\n-{3,}\s*$/, '').trim()
  return {
    lead:     tidy(lead),
    sections: sections.map(s => ({ heading: s.heading, body: tidy(s.lines) })),
  }
}

/** The section whose heading names `path` in backticks: ## Deliveries (`/deliveries`). */
export function sectionForPath(sections: MarkdownSection[], path: string): MarkdownSection | undefined {
  // The page's own section names its route alone: "Coldroom (`/coldroom`)". A
  // section ABOUT a tab of that page names it too ("Cold treatment for the USA
  // (`/coldroom`, Cold treatment tab)") and comes first in the file, so take the
  // route-alone heading first.
  return sections.find(s => s.heading.endsWith('(`' + path + '`)'))
    ?? sections.find(s => s.heading.includes('`' + path + '`'))
}

/**
 * Point the manual's links at the app. A link that resolves to another
 * tile's section becomes /training/<tile id>; anything else — a runbook, the
 * chapter index, an anchor to a step inside this section — is unwrapped to its
 * text, because a path into the repo leads nowhere on a phone. Web links stay.
 * `resolve` gets the link's chapter file ('' = this chapter) and its anchor.
 */
export function rewriteManualLinks(md: string, resolve: (file: string, slug: string) => string | undefined): string {
  return md.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, text: string, href: string) => {
    if (/^https?:\/\//.test(href)) return whole
    const m = href.match(/^(?:\.\/)?([^#]*)(?:#(.+))?$/)
    const to = m?.[2] ? resolve(m[1], m[2]) : undefined
    return to ? `[${text}](${to})` : text
  })
}

// ---------------------------------------------------------------------------
// Afrikaans — docs/manuals/af/ and src/help/topics-af/ (see the README there)
// ---------------------------------------------------------------------------

export type TrainingLang = 'en' | 'af'

/**
 * For an Afrikaans request: 'fresh' was translated from today's English,
 * 'stale' from an older one (the English has changed since), 'missing' has no
 * Afrikaans yet, so the English is shown.
 */
export type Freshness = 'fresh' | 'stale' | 'missing'

/** Which language a guide actually came out in, and how far to trust it. */
export interface GuideLanguage {
  shown:     TrainingLang
  freshness: Freshness
  /** A native reader has signed the Afrikaans off. Always true for English. */
  reviewed:  boolean
}

const ENGLISH: GuideLanguage = { shown: 'en', freshness: 'fresh', reviewed: true }
const ENGLISH_NOT_TRANSLATED: GuideLanguage = { shown: 'en', freshness: 'missing', reviewed: true }

const afChapterFiles = import.meta.glob(
  [
    '/docs/manuals/af/PACKHOUSE-*.md',
    '/docs/manuals/af/FARM-*.md',
    '/docs/manuals/af/EXPORTER-*.md',
    '/docs/manuals/af/GETTING-STARTED.md',
  ],
  { query: '?raw', import: 'default' },
) as Record<string, () => Promise<string>>
const enTopicFiles = import.meta.glob('/src/help/topics/*.md', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>
const afTopicFiles = import.meta.glob('/src/help/topics-af/*.af.md', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>

function markerOf(sectionText: string) {
  for (const line of sectionText.split('\n').slice(1, 4)) {
    const mk = parseMarker(line)
    if (mk) return mk
  }
  return null
}

const stripMarkers = (md: string) => md.split('\n').filter(l => !AF_MARKER.test(l.trim())).join('\n')

/**
 * The Afrikaans twin of one English section: found by the English heading's
 * slug in its marker, fresh while the marker's fingerprint is today's English.
 * index -1 = no twin.
 */
export function afTwin(
  en: { heading: string; text: string },
  af: { heading: string; text: string }[],
): { index: number; freshness: Freshness; reviewed: boolean } {
  const slug = headingSlug(en.heading)
  const index = af.findIndex(s => markerOf(s.text)?.slug === slug)
  if (index < 0) return { index, freshness: 'missing', reviewed: false }
  const mk = markerOf(af[index].text)!
  return { index, freshness: mk.hash === fingerprint(en.text) ? 'fresh' : 'stale', reviewed: mk.reviewed }
}

// ---------------------------------------------------------------------------
// Loaders
// ---------------------------------------------------------------------------

export interface TrainingGuide {
  /** The chapter's title line, e.g. "Packhouse manual 1 — Intake & Storage". */
  chapter:   string
  /** Everything before the section's first step — What it's for, Who can use it. */
  introHtml: string
  /** One per `###` — Step by step…, Fixing mistakes, Troubleshooting. */
  parts:     { title: string; html: string }[]
  language:  GuideLanguage
}

type Chapter = { file: string; title: string; lead: string; sections: MarkdownSection[]; raw: string }

async function loadChapters(files: string[]): Promise<Chapter[]> {
  const entries = Object.entries(chapterFiles).filter(([p]) => files.includes(fileOf(p)))
  const texts = await Promise.all(entries.map(([, load]) => load()))
  return entries.map(([p], i) => {
    const { lead, sections } = splitSections(texts[i], 2)
    const title = lead.match(/^# (.+)$/m)?.[1].trim() ?? fileOf(p)
    return { file: fileOf(p), title, lead, sections, raw: texts[i] }
  })
}

/** The Afrikaans chapter beside an English one, cut the same way — or null. */
async function loadAfChapter(file: string) {
  const load = afChapterFiles[`/docs/manuals/af/${file}`]
  if (!load) return null
  const text = await load()
  const clean = splitSections(stripMarkers(text), 2)
  return { raw: rawSections(text), clean, title: clean.lead.match(/^# (.+)$/m)?.[1].trim() }
}

/** The Afrikaans twin of one English section, ready to render — or null when there is none. */
async function afrikaansSection(ch: Chapter, heading: string) {
  const af = await loadAfChapter(ch.file)
  const enRaw = rawSections(ch.raw).find(s => s.heading === heading)
  if (!af || !enRaw) return null
  const twin = afTwin(enRaw, af.raw)
  const body = twin.index >= 0 ? af.clean.sections[twin.index]?.body : undefined
  if (body === undefined) return null
  const language: GuideLanguage = { shown: 'af', freshness: twin.freshness, reviewed: twin.reviewed }
  return { body, chapter: af.title ?? ch.title, language }
}

/** chapter file + heading slug → the tile whose section that heading opens. */
function sectionLinkTargets(chapters: Chapter[], kind: ModuleOwningKind): Map<string, string> {
  const targets = new Map<string, string>()
  for (const ch of chapters) {
    for (const s of ch.sections) {
      const path = s.heading.match(/`(\/[^`]*)`/)?.[1]
      const tile = path && MODULES.find(m => m.path === path && moduleKindOfCategory(m.category) === kind)
      if (tile) targets.set(`${ch.file}#${headingSlug(s.heading)}`, tile.id)
    }
  }
  return targets
}

async function renderGuide(
  chapter: string, intro: string, parts: MarkdownSection[],
  resolve: (file: string, slug: string) => string | undefined,
  language: GuideLanguage,
): Promise<TrainingGuide> {
  // marked is imported lazily so it stays out of the eager main bundle, the
  // same as loadHelpTopic. The manuals are repo-authored, PR-reviewed content —
  // the help topics' trust level — so the HTML needs no sanitiser.
  const { marked } = await import('marked')
  const html = (md: string) => marked.parse(rewriteManualLinks(md, resolve), { async: false }) as string
  return {
    chapter,
    introHtml: html(intro),
    // "Signing in (`/login`)" → "Signing in": the route is for the repo's
    // readers; the app already knows where it is.
    parts:     parts.map(p => ({ title: p.heading.replace(/\s*\(`[^`]*`\)/g, ''), html: html(p.body) })),
    language,
  }
}

/**
 * A tile's section of the manual, split into its intro and its steps. Loads
 * only the chapters of the tile's own app. Resolves to null when the tile's
 * app has no chapters here or no heading names its route. In Afrikaans, the
 * twin section when there is one — its links resolve exactly like the
 * English, because an Afrikaans page heading keeps the English one.
 */
export async function loadTileGuide(tile: ModuleDef, lang: TrainingLang = 'en'): Promise<TrainingGuide | null> {
  const kind = moduleKindOfCategory(tile.category)
  const prefix = CHAPTER_PREFIX[kind]
  if (!prefix) return null
  const chapters = await loadChapters(Object.keys(chapterFiles).map(fileOf).filter(f => f.startsWith(prefix)))
  for (const ch of chapters) {
    const section = sectionForPath(ch.sections, tile.path)
    if (!section) continue
    const targets = sectionLinkTargets(chapters, kind)
    const resolve = (file: string, slug: string) => {
      const id = targets.get(`${file || ch.file}#${slug}`)
      return id && id !== tile.id ? `/training/${id}` : undefined
    }
    const af = lang === 'af' ? await afrikaansSection(ch, section.heading) : null
    if (af) {
      const { lead, sections } = splitSections(af.body, 3)
      return renderGuide(af.chapter, lead, sections, resolve, af.language)
    }
    const { lead, sections } = splitSections(section.body, 3)
    return renderGuide(ch.title, lead, sections, resolve, lang === 'af' ? ENGLISH_NOT_TRANSLATED : ENGLISH)
  }
  return null
}

/** The Getting started chapter, one part per `##` section (its Contents dropped). */
export async function loadGettingStartedGuide(lang: TrainingLang = 'en'): Promise<TrainingGuide | null> {
  const [ch] = await loadChapters([GETTING_STARTED_FILE])
  if (!ch) return null
  // The lead's blockquote points at the repo's manual index; the page is the index here.
  const introOf = (lead: string) => lead.split('\n').filter(l => !l.startsWith('# ') && !l.startsWith('>')).join('\n')
  const parts = ch.sections.filter(s => s.heading !== 'Contents')
  const af = lang === 'af' ? await loadAfChapter(GETTING_STARTED_FILE) : null
  if (!af) return renderGuide(ch.title, introOf(ch.lead), parts, () => undefined, lang === 'af' ? ENGLISH_NOT_TRANSLATED : ENGLISH)

  // Part by part: a section with no fresh twin shows in English, and one such
  // part is enough for the page to say the English has moved on.
  const enRaw = rawSections(ch.raw)
  let allFresh = true
  let reviewed = true
  const mixed = parts.map(p => {
    const twin = afTwin(enRaw.find(s => s.heading === p.heading)!, af.raw)
    if (twin.freshness !== 'fresh') allFresh = false
    reviewed &&= twin.reviewed
    return twin.index >= 0 ? af.clean.sections[twin.index] : p
  })
  return renderGuide(af.title ?? ch.title, introOf(af.clean.lead), mixed, () => undefined,
    { shown: 'af', freshness: allFresh ? 'fresh' : 'stale', reviewed })
}

/** A tile's quick guide (its help topic) in the language asked for, where it exists. */
export async function loadTrainingTopic(
  id: string, lang: TrainingLang,
): Promise<{ title: string; html: string; language: GuideLanguage } | null> {
  const en = await loadHelpTopic(id)
  if (!en) return null
  const loadAf = lang === 'af' ? afTopicFiles[`/src/help/topics-af/${id}.af.md`] : undefined
  const loadEn = enTopicFiles[`/src/help/topics/${id}.md`]
  if (!loadAf || !loadEn) return { title: en.title, html: en.html, language: lang === 'af' ? ENGLISH_NOT_TRANSLATED : ENGLISH }
  const [afRaw, enRaw, { marked }] = await Promise.all([loadAf(), loadEn(), import('marked')])
  const { meta, body } = parseFrontmatter(afRaw)
  return {
    title:    meta.title ?? en.title,
    html:     marked.parse(body, { async: false }) as string,
    language: { shown: 'af', freshness: meta.source === fingerprint(enRaw) ? 'fresh' : 'stale', reviewed: meta.reviewed === 'yes' },
  }
}
